import React from 'react';
import { Outlet, useMatch, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCollapseSidebarOnMount } from 'hooks';
import { useIsMobile } from 'hooks/use-media-query';
import * as TestModule from 'modules/test';
import { WorkspaceLiveProvider } from 'components/workspace/WorkspaceLive';
import { cn } from 'lib/utils';
import { TestHeader } from './TestHeader';
import { TestConfigSidebar } from './TestConfigSidebar';
import { TestLifecycleProvider } from './TestLifecycleContext';
import { TestPhoneActivateBar, TestPhoneHeader, TestPhoneSteps } from './TestPhoneHeader';

/**
 * The test workspace. Puts the shell into focus mode so the global nav and
 * header step aside: the workspace header + lifecycle strip span the top, the
 * rail and a white page sit below. Phones get their own 56px header, a step
 * bar while the test is a draft, and the Overview's sticky Activate bar.
 */
export const TestScopedContent: React.FC = () => {
  const { t } = useTranslation();
  const { testId } = useParams<{ testId: string }>();
  const isMobile = useIsMobile();
  const onOverview = Boolean(useMatch({ path: '/app/tests/:testId', end: true }));
  const { item: test, isLoading } = TestModule.Hooks.useSingle({
    id: testId || '',
    enabled: Boolean(testId),
  });
  useCollapseSidebarOnMount();

  if (!testId) {
    return null;
  }

  const ready = !isLoading && Boolean(test?.id);

  return (
    <TestLifecycleProvider>
      <WorkspaceLiveProvider>
        {/* Desktop pages sit on white; phone pages on the page ground, under white cards. */}
        <div className={cn('flex h-full flex-col overflow-hidden', isMobile ? 'bg-background' : 'bg-card')}>
          {isMobile ? (
            <>
              <TestPhoneHeader />
              {ready && <TestPhoneSteps />}
            </>
          ) : (
            <TestHeader />
          )}
          <div className="flex min-h-0 flex-1 overflow-hidden">
            <TestConfigSidebar />
            <main className="min-w-0 flex-1 overflow-y-auto px-4 py-3 scrollbar-thin md:px-6 md:pb-6 md:pt-5">
              {isLoading ? (
                <div className="flex min-h-[400px] items-center justify-center">
                  <div className="h-10 w-10 animate-spin rounded-full border-4 border-muted border-t-foreground" />
                </div>
              ) : !test?.id ? (
                <div className="flex min-h-[400px] items-center justify-center text-muted-foreground">
                  {t('test_not_found')}
                </div>
              ) : (
                <Outlet />
              )}
            </main>
          </div>
          {isMobile && onOverview && ready && <TestPhoneActivateBar />}
        </div>
      </WorkspaceLiveProvider>
    </TestLifecycleProvider>
  );
};

export default TestScopedContent;
